import { useEffect, useId, useMemo, useRef, useState } from "react"
import cx from "clsx"
import CloseIcon from "../assets/icon-close.svg?react"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./MultiSelectField.module.css"

export type MultiSelectOption = { id: string; name: string }

/**
 * Picks several options from a list too long to show at once — an editable
 * combobox (WAI-ARIA APG) that filters as it is typed, with what has been picked
 * shown as removable chips above it.
 *
 * A checkbox fieldset is the simpler control and the one the design drew, but
 * there are 52 programmes and 148 service strategies to choose between, which is
 * past what a fieldset reads as.
 */
export function MultiSelectField({
  label,
  options,
  selectedIds,
  onChange,
  placeholder,
  disabled = false,
  error,
  hint,
  required = false,
  emptyMessage = "No matches.",
}: {
  label: string
  options: MultiSelectOption[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
  disabled?: boolean
  error?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  required?: boolean
  emptyMessage?: string
}) {
  const inputId = useId()
  const listboxId = useId()
  const errorId = useId()
  const hintId = useId()
  const optionIdPrefix = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const controlRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  // What changed, for a screen reader. Picking happens without moving focus, so
  // nothing else would say that a chip appeared.
  const [announcement, setAnnouncement] = useState("")

  const selected = useMemo(
    () =>
      selectedIds
        .map((id) => options.find((o) => o.id === id))
        .filter((o): o is MultiSelectOption => o !== undefined),
    [selectedIds, options],
  )

  // Already-picked options leave the list: picking one twice is not a thing the
  // control can express, and the chips are where they are taken back out.
  const matches = useMemo(() => {
    const wanted = query.trim().toLowerCase()
    return options.filter(
      (o) =>
        !selectedIds.includes(o.id) && o.name.toLowerCase().includes(wanted),
    )
  }, [options, selectedIds, query])

  const expanded = open && matches.length > 0
  const activeOption = expanded
    ? matches[Math.min(activeIndex, matches.length - 1)]
    : undefined
  const showEmpty = open && matches.length === 0

  // Arrowing past the visible rows should bring the row into view, not walk off
  // the bottom of a list 52 long.
  useEffect(() => {
    // Optional call: jsdom has no scrollIntoView, and a missing scroll is not a
    // reason for the control to throw.
    listboxRef.current
      ?.querySelector("[data-active]")
      ?.scrollIntoView?.({ block: "nearest" })
  }, [activeIndex, expanded])

  function pick(option: MultiSelectOption) {
    onChange([...selectedIds, option.id])
    setQuery("")
    setActiveIndex(0)
    // The list closes on a pick rather than staying up for the next one: it is
    // positioned over whatever follows the field, and a form has a button there.
    // Focus stays in the search box, so the next keystroke or click brings it
    // back.
    setOpen(false)
    setAnnouncement(`${option.name} added`)
    inputRef.current?.focus()
  }

  function remove(option: MultiSelectOption) {
    onChange(selectedIds.filter((id) => id !== option.id))
    setAnnouncement(`${option.name} removed`)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const last = matches.length - 1

    if (event.key === "Escape") {
      // Closes the list first; a second press clears what was typed, so Escape
      // always undoes the most recent thing.
      if (open) setOpen(false)
      else setQuery("")
      return
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        // Opening lands on an end of the list rather than stepping from where a
        // previous visit left off.
        setActiveIndex(event.key === "ArrowDown" || event.altKey ? 0 : last)
        return
      }
      if (event.altKey) return
      setActiveIndex((i) => {
        if (event.key === "ArrowDown") return i >= last ? 0 : i + 1
        return i <= 0 ? last : i - 1
      })
      return
    }

    if ((event.key === "Home" || event.key === "End") && expanded) {
      event.preventDefault()
      setActiveIndex(event.key === "Home" ? 0 : last)
      return
    }

    if (event.key === "Enter" && activeOption) {
      event.preventDefault()
      pick(activeOption)
      return
    }

    // Backspace on an empty box takes the last chip back, the way a token input
    // is expected to behave.
    if (event.key === "Backspace" && query === "" && selected.length > 0) {
      remove(selected[selected.length - 1])
    }
  }

  return (
    <div className={cx(styles.field, { [styles.invalid]: error })}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
        {required && (
          <span aria-hidden="true" className={styles.required}>
            {" "}
            *
          </span>
        )}
      </label>
      {hint && <FieldHint id={hintId}>{hint}</FieldHint>}

      {selected.length > 0 && (
        <ul
          aria-label={`Selected ${label.toLowerCase()}`}
          className={styles.chips}
        >
          {selected.map((option) => (
            <li key={option.id} className={styles.chip}>
              {option.name}
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove ${option.name}`}
                disabled={disabled}
                onClick={() => remove(option)}
              >
                <CloseIcon className={styles.removeIcon} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div ref={controlRef} className={styles.control}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          className={styles.input}
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? listboxId : undefined}
          aria-activedescendant={
            activeOption ? `${optionIdPrefix}-${activeOption.id}` : undefined
          }
          aria-invalid={error ? true : undefined}
          // Not \`required\`: the box holds the search, which empties on every
          // pick, so native validation would judge the wrong thing.
          aria-required={required || undefined}
          aria-describedby={describedBy(hint && hintId, error && errorId)}
          placeholder={placeholder}
          disabled={disabled}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActiveIndex(0)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          // After a pick the box keeps focus, so clicking it fires no focus event.
          onClick={() => setOpen(true)}
          // Moving to an option is not leaving the control, so the list stays up
          // long enough for the click that follows to land.
          onBlur={(e) => {
            if (!controlRef.current?.contains(e.relatedTarget)) setOpen(false)
          }}
          onKeyDown={onKeyDown}
        />

        {expanded && (
          <ul
            ref={listboxRef}
            role="listbox"
            id={listboxId}
            aria-label={label}
            className={styles.listbox}
          >
            {matches.map((option, i) => (
              <li
                key={option.id}
                id={`${optionIdPrefix}-${option.id}`}
                role="option"
                className={styles.option}
                data-active={option.id === activeOption?.id ? "" : undefined}
                // Keeps focus in the search box, so the blur above does not
                // close the list before the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => pick(option)}
              >
                {option.name}
              </li>
            ))}
          </ul>
        )}

        {showEmpty && <p className={styles.empty}>{emptyMessage}</p>}
      </div>

      {error && (
        <span id={errorId} className={styles.error} aria-live="polite">
          {error}
        </span>
      )}

      <span role="status" aria-live="polite" className={styles.srOnly}>
        {announcement}
      </span>
    </div>
  )
}
